import { z } from 'zod';
import { isIsoDate } from './sales-dates.js';
import { SALES_COMPARE_MODES, SALES_GRANULARITIES, SALES_RANGE_PRESETS, autoGranularity, compareRange, resolveRange, } from './sales-periods.js';
/**
 * The dashboard's filters: one schema for the URL, the web client and the API,
 * so a link copied from the browser asks the server exactly what the screen
 * showed.
 *
 * Everything travels as flat string parameters (the URL hash on the web, the
 * query string on the API). Defaults are left out when serializing, so a
 * shared link stays short.
 */
export const SALES_SITE_SCOPES = ['combined', 'mlf', 'mff'];
/**
 * `all` counts every order as recognized, open review questions at their
 * conservative default. `validated` leaves out orders still waiting on review.
 */
export const SALES_REVIEW_MODES = ['all', 'validated'];
const isoDate = z.string().refine(isIsoDate, 'Expected a date as YYYY-MM-DD');
const flag = z.preprocess((value) => {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'string')
        return value === 'true' || value === '1';
    return undefined;
}, z.boolean().optional());
const idList = z.preprocess((value) => {
    if (value === undefined || value === null || value === '')
        return undefined;
    const list = Array.isArray(value) ? value : String(value).split(',');
    const ids = list.map((v) => String(v).trim()).filter(Boolean);
    return ids.length > 0 ? ids : undefined;
}, z.array(z.string().min(1).max(64)).max(50).optional());
export const salesFiltersSchema = z.object({
    preset: z.enum(SALES_RANGE_PRESETS).catch('last_30d').default('last_30d'),
    from: isoDate.optional().catch(undefined),
    to: isoDate.optional().catch(undefined),
    granularity: z.enum(['auto', ...SALES_GRANULARITIES]).catch('auto').default('auto'),
    site: z.enum(SALES_SITE_SCOPES).catch('combined').default('combined'),
    /** Restrict to these channels. Absent means every channel with valid data. */
    channelIds: idList.catch(undefined),
    compare: z.enum(SALES_COMPARE_MODES).catch('previous_year').default('previous_year'),
    alignWeekdays: flag.catch(undefined),
    review: z.enum(SALES_REVIEW_MODES).catch('all').default('all'),
    /** Show channels whose history only partly covers the range, flagged, rather than leaving them out. */
    includePartial: flag.catch(undefined),
});
export const DEFAULT_SALES_FILTERS = salesFiltersSchema.parse({});
/** Reads filters from any flat string map (URLSearchParams entries, a Fastify query). Never throws: bad values fall back to defaults. */
export function parseSalesFilters(input) {
    const record = input instanceof URLSearchParams ? Object.fromEntries(input.entries()) : input;
    return salesFiltersSchema.parse(record);
}
const FILTER_KEYS = Object.keys(salesFiltersSchema.shape);
/** Only what differs from the defaults, in a stable order. */
export function salesFiltersToParams(filters) {
    const params = new URLSearchParams();
    for (const key of FILTER_KEYS) {
        const value = filters[key];
        if (value === undefined || value === false)
            continue;
        if (key !== 'from' && key !== 'to' && value === DEFAULT_SALES_FILTERS[key])
            continue;
        if ((key === 'from' || key === 'to') && filters.preset !== 'custom')
            continue;
        params.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
    return params;
}
export function resolveSalesFilters(filters, today) {
    const range = resolveRange(filters.preset, today, { from: filters.from, to: filters.to });
    const auto = autoGranularity(range);
    // A chosen grain finer than the range can sensibly draw (days across five
    // years) falls back to the automatic one rather than thousands of bars.
    const chosen = filters.granularity === 'auto' ? auto : filters.granularity;
    const granularity = SALES_GRANULARITIES.indexOf(chosen) < SALES_GRANULARITIES.indexOf(auto) - 1 ? auto : chosen;
    return {
        range,
        compare: compareRange(range, filters.compare, { preset: filters.preset, alignWeekdays: filters.alignWeekdays }),
        granularity,
    };
}
//# sourceMappingURL=sales-filters.js.map